import { useEffect, useId, useState } from "react";
import { Minus, Plus } from "lucide-react";

type NumberFieldProps = {
  decreaseLabel?: (label: string) => string;
  error?: string;
  increaseLabel?: (label: string) => string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (value: number) => void;
};

export function NumberField({
  decreaseLabel = (label) => `${label}を減らす`,
  error,
  increaseLabel = (label) => `${label}を増やす`,
  label,
  value,
  min,
  max,
  step,
  unit = "mm",
  onChange
}: NumberFieldProps) {
  const errorId = useId();
  const [draftValue, setDraftValue] = useState(() => formatNumber(value, step));
  const [isFocused, setIsFocused] = useState(false);
  const decimalPlaces = step.toString().includes(".") ? step.toString().split(".")[1].length : 0;

  useEffect(() => {
    if (!isFocused) {
      setDraftValue(formatNumber(value, step));
    }
  }, [isFocused, step, value]);

  const updateFromText = (rawValue: string) => {
    setDraftValue(rawValue);

    if (rawValue.trim() === "" || rawValue === "-" || rawValue === "." || rawValue === "-.") {
      onChange(Number.NaN);
      return;
    }

    const nextValue = Number(rawValue);

    onChange(Number.isFinite(nextValue) ? nextValue : Number.NaN);
  };

  const stepValue = (direction: -1 | 1) => {
    const baseValue = Number.isFinite(value) ? value : min;
    const nextValue = roundToStep(clampNumber(baseValue + direction * step, min, max), step);

    setDraftValue(formatNumber(nextValue, step));
    onChange(nextValue);
  };

  return (
    <label className={`field${error ? " field--invalid" : ""}`}>
      <span className="field__label">{label}</span>
      <span className="field__body">
        <span className="field__control">
          <button
            aria-label={decreaseLabel(label)}
            className="icon-button"
            type="button"
            onClick={() => stepValue(-1)}
          >
            <Minus size={16} />
          </button>
          <input
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? "true" : undefined}
            inputMode={decimalPlaces > 0 ? "decimal" : "numeric"}
            type="text"
            value={draftValue}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => updateFromText(event.target.value)}
            onFocus={() => setIsFocused(true)}
          />
          <span className="field__unit">{unit}</span>
          <button
            aria-label={increaseLabel(label)}
            className="icon-button"
            type="button"
            onClick={() => stepValue(1)}
          >
            <Plus size={16} />
          </button>
        </span>
        {error ? (
          <span className="field__error" id={errorId}>
            {error}
          </span>
        ) : null}
      </span>
    </label>
  );
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function formatNumber(value: number, step: number): string {
  if (!Number.isFinite(value)) {
    return "";
  }

  const decimals = step.toString().includes(".") ? step.toString().split(".")[1].length : 0;

  return Number.isInteger(value) ? value.toString() : value.toFixed(decimals);
}

function roundToStep(value: number, step: number): number {
  const decimals = step.toString().includes(".") ? step.toString().split(".")[1].length : 0;

  return Number(value.toFixed(decimals));
}
